import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import NavTabs from '../components/NavTabs';
import { useAuth } from '../context/AuthContext';
import FacilityCard from '../components/FacilityCard';
import { getFavorites, removeFavorite } from '../api';

function formatDate(ts) {
  if (!ts) return '';
  return new Date(ts).toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

export default function Favorites() {
  const { user, isOrgAccount } = useAuth();
  const [favorites, setFavorites] = useState([]);
  const [loading, setLoading]     = useState(true);
  const [error, setError]         = useState('');

  const pageTitle = isOrgAccount ? 'Our Favorites' : 'My Favorites';

  useEffect(() => {
    getFavorites()
      .then(setFavorites)
      .catch((err) => setError(
        err?.response?.status === 401
          ? 'Your session has expired. Please log in again.'
          : 'Could not load favorites. Please try again.'
      ))
      .finally(() => setLoading(false));
  }, []);

  const handleRemove = async (facilityId) => {
    try {
      await removeFavorite(facilityId);
      setFavorites((prev) => prev.filter((f) => f.facility.id !== facilityId));
    } catch { /* ignore */ }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Header
        subtitle={isOrgAccount && user?.organization_name ? user.organization_name : undefined}
        right={null}
      />
      <NavTabs />

      <main className="max-w-5xl mx-auto px-4 py-8">
        <h1 className="text-2xl font-bold text-gray-900 mb-6">{pageTitle}</h1>

        {loading ? (
          <div className="flex justify-center py-16">
            <svg className="animate-spin w-8 h-8 text-blue-400" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : favorites.length === 0 ? (
          <div className="flex flex-col items-center py-20 text-center">
            <svg className="w-14 h-14 text-gray-300 mb-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5}
                d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <p className="text-gray-500 font-medium">No saved facilities yet</p>
            <p className="text-gray-400 text-sm mt-1">
              Click the heart icon on any facility card to save it here.
            </p>
            <Link to="/" className="mt-4 text-sm text-blue-600 hover:underline font-medium">
              Browse facilities
            </Link>
          </div>
        ) : (
          <>
            <p className="text-sm text-gray-500 mb-5">
              {favorites.length} saved {favorites.length === 1 ? 'facility' : 'facilities'}
            </p>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
              {favorites.map(({ facility, added_by_name, created_at }) => (
                <div key={facility.id} className="relative">
                  {/* Added-by attribution */}
                  {(isOrgAccount || added_by_name) && (
                    <p className="text-xs text-gray-400 mb-1 pl-1">
                      Added by {added_by_name || 'you'} — {formatDate(created_at)}
                    </p>
                  )}

                  <FacilityCard
                    facility={facility}
                    isFavorited={true}
                    addedByColleague={
                      isOrgAccount && added_by_name !== `${user?.first_name} ${user?.last_name}`.trim()
                        ? added_by_name
                        : null
                    }
                  />

                  {/* Remove button */}
                  <button
                    onClick={() => handleRemove(facility.id)}
                    className="absolute top-3 right-3 text-xs text-gray-400 hover:text-red-500 underline underline-offset-2 transition-colors"
                    title="Remove from favorites"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
